import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowRight, Search, Filter, BookOpen, Laptop, Sprout, Palette, Trophy, Users, X } from 'lucide-react';
import { supabase } from '@/src/lib/supabase';
import { Activity } from '@/src/types';

export const Activities = () => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [filteredActivities, setFilteredActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('Todos');
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const { data, error } = await supabase
          .from('activities')
          .select('*')
          .eq('status', 'active')
          .order('created_at', { ascending: false });

        if (error) throw error;
        const fetchedData = data || [];
        setActivities(fetchedData);
        setFilteredActivities(fetchedData);
      } catch (err) {
        console.error('Error fetching activities:', err);
        const mockData = [
          { id: '1', title: 'Laboratório de Informática', description: 'Uso das tecnologias para garantir uma base de conhecimento digital sólida para os estudantes.', created_at: '', status: 'active' },
          { id: '2', title: 'Educação no Campo', description: 'Atuação na cooperação agrícola para aprender novas formas de desenvolvimento do meio rural.', created_at: '', status: 'active' },
          { id: '3', title: 'Protagonismo Juvenil', description: 'Atividades que incentivam o estudante a ser agente de transformação na sua realidade.', created_at: '', status: 'active' },
          { id: '4', title: 'Artes e Cultura', description: 'Desenvolvimento de habilidades criativas e valorização do patrimônio cultural local.', created_at: '', status: 'active' },
          { id: '5', title: 'Esportes e Lazer', description: 'Promoção da saúde e integração social através de práticas esportivas coletivas.', created_at: '', status: 'active' },
          { id: '6', title: 'Projetos de Leitura', description: 'Incentivo ao hábito da leitura e desenvolvimento da argumentação sólida.', created_at: '', status: 'active' },
        ];
        setActivities(mockData);
        setFilteredActivities(mockData);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, []);

  useEffect(() => {
    let result = activities;

    if (searchTerm) {
      result = result.filter(a => 
        a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (activeFilter !== 'Todos') {
      // Simple logic for mock filters
      if (activeFilter === 'Tecnologia') result = result.filter(a => a.title.includes('Informática'));
      if (activeFilter === 'Social') result = result.filter(a => a.title.includes('Juvenil') || a.title.includes('Campo'));
      if (activeFilter === 'Cultura') result = result.filter(a => a.title.includes('Artes') || a.title.includes('Leitura'));
    }

    setFilteredActivities(result);
  }, [searchTerm, activeFilter, activities]);

  const getIcon = (title: string) => {
    if (title.includes('Informática')) return <Laptop />;
    if (title.includes('Campo')) return <Sprout />;
    if (title.includes('Juvenil')) return <Users />;
    if (title.includes('Artes')) return <Palette />;
    if (title.includes('Esportes')) return <Trophy />;
    return <BookOpen />;
  };

  return (
    <div className="pb-32">
      {/* Header */}
      <section className="bg-white py-24 border-b border-slate-100">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl space-y-6">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl md:text-7xl font-bold text-primary tracking-tight"
            >
              Nossos <span className="text-secondary italic">Projetos</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-slate-600 leading-relaxed"
            >
              Conheça as atividades e projetos pedagógicos que desenvolvemos para garantir uma educação de qualidade.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="container mx-auto px-4 md:px-6 mt-12">
        <div className="flex flex-col md:flex-row gap-6 items-center justify-between bg-slate-50 p-6 rounded-3xl border border-slate-100">
          <div className="relative w-full md:max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              placeholder="Buscar atividades..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-6 py-3 rounded-2xl bg-white border-none focus:ring-2 focus:ring-secondary transition-all outline-none text-primary"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto">
            {['Todos', 'Tecnologia', 'Social', 'Cultura'].map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-6 py-3 rounded-xl font-bold text-sm transition-all whitespace-nowrap ${
                  activeFilter === filter 
                    ? 'bg-primary text-white shadow-lg shadow-primary/20' 
                    : 'bg-white text-slate-500 hover:bg-slate-100'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="container mx-auto px-4 md:px-6 mt-12">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-slate-100 animate-pulse rounded-2xl aspect-[4/5]" />
            ))}
          </div>
        ) : filteredActivities.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredActivities.map((activity, i) => (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="group bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 flex flex-col"
              >
                <div className="aspect-video bg-slate-50 overflow-hidden relative flex items-center justify-center">
                  <div className="text-secondary opacity-20 group-hover:opacity-40 transition-opacity transform group-hover:scale-110 duration-500">
                    {React.cloneElement(getIcon(activity.title) as React.ReactElement, { size: 120 })}
                  </div>
                  <div className="absolute top-4 right-4 bg-primary text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest">
                    Pedagógico
                  </div>
                </div>
                <div className="p-10 flex-grow flex flex-col">
                  <h3 className="text-2xl font-bold text-primary mb-4 group-hover:text-secondary transition-colors">
                    {activity.title}
                  </h3>
                  <p className="text-slate-500 leading-relaxed mb-8 flex-grow">
                    {activity.description}
                  </p>
                  <button 
                    onClick={() => setSelectedActivity(activity)}
                    className="inline-flex items-center gap-2 font-bold text-primary group-hover:gap-4 transition-all"
                  >
                    Saiba mais
                    <ArrowRight size={18} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-32 space-y-4">
            <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto text-slate-300">
              <Search size={40} />
            </div>
            <h3 className="text-2xl font-bold text-primary">Nenhum resultado encontrado</h3>
            <p className="text-slate-500">Tente buscar por outros termos ou mudar o filtro.</p>
          </div>
        )}
      </section>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedActivity && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedActivity(null)}
              className="absolute inset-0 bg-primary/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-2xl bg-white rounded-[2.5rem] overflow-hidden shadow-2xl"
            >
              <button 
                onClick={() => setSelectedActivity(null)}
                className="absolute top-6 right-6 w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center text-slate-400 hover:text-primary transition-colors z-10"
              >
                <X size={20} />
              </button>
              
              <div className="aspect-video bg-slate-50 flex items-center justify-center relative">
                <div className="text-secondary opacity-20">
                  {React.cloneElement(getIcon(selectedActivity.title) as React.ReactElement, { size: 160 })}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-white to-transparent">
                  <div className="bg-primary text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest inline-block mb-4">
                    Projeto Pedagógico
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-primary tracking-tight">
                    {selectedActivity.title}
                  </h2>
                </div>
              </div>
              
              <div className="p-8 md:p-12 space-y-6">
                <p className="text-slate-600 text-lg leading-relaxed">
                  {selectedActivity.description}
                </p>
                <div className="pt-6 border-t border-slate-100 flex flex-col sm:flex-row gap-4">
                  <div className="flex items-center gap-3 text-slate-500">
                    <div className="w-10 h-10 bg-slate-50 rounded-lg flex items-center justify-center text-primary">
                      <Users size={18} />
                    </div>
                    <span className="text-sm font-medium">Público: Estudantes e Comunidade</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-500">
                    <div className="w-10 h-10 bg-slate-50 rounded-lg flex items-center justify-center text-primary">
                      <BookOpen size={18} />
                    </div>
                    <span className="text-sm font-medium">Frequência: Semanal</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
